import type { ClientToServer, ServerToClient, UserProfile, UserSettings } from "@lupp/shared";
import { getSocket } from "./socket";

export function authenticate(): Promise<UserProfile> {
  const socket = getSocket();

  return new Promise<UserProfile>((resolve, reject) => {
    const onEvent = (event: ServerToClient): void => {
      if (event.type === "auth_ok") {
        cleanup();
        resolve(event.user);
      } else if (event.type === "auth_error") {
        cleanup();
        reject(new Error(event.message));
      }
    };

    const cleanup = (): void => {
      socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
    };

    // Register listener before calling getAccessToken so the response is never missed.
    socket.on("event", onEvent as Parameters<typeof socket.on>[1]);

    void (Office.auth as { getAccessToken(): Promise<string> })
      .getAccessToken()
      .then((entraToken) => {
        socket.emit("auth_token", { type: "auth_token", entraToken } satisfies ClientToServer);
      })
      .catch((err: unknown) => {
        cleanup();
        // Office-SSO-Fehler sind Objekte mit code/message/name — sonst landet nur "[object Object]"
        // im Log. Echten Code + Message herausziehen, damit der Fehler diagnostizierbar ist.
        if (err instanceof Error) {
          reject(err);
        } else if (err && typeof err === "object") {
          const e = err as { code?: unknown; name?: unknown; message?: unknown };
          reject(new Error(`Office SSO getAccessToken failed (code ${String(e.code)}, ${String(e.name)}): ${String(e.message)}`));
        } else {
          reject(new Error(String(err)));
        }
      });
  });
}

export function subscribeUserSettings(onSettings: (settings: UserSettings) => void): () => void {
  const socket = getSocket();

  const onEvent = (event: ServerToClient): void => {
    if (event.type === "user_settings") {
      onSettings(event.settings);
    }
  };

  socket.on("event", onEvent as Parameters<typeof socket.on>[1]);

  return () => {
    socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
  };
}

/**
 * Re-authenticates on every socket RECONNECT (not the first connect). The server keeps
 * session context in-memory per socket.id; after a server restart or any reconnect the
 * socket gets a new id with no context, so we must send auth_token again — otherwise
 * persistence/memory/attachments (which need the user JWT) silently stop working.
 */
export function onSocketReconnect(handler: () => void): () => void {
  const socket = getSocket();
  // If already connected when we attach, the next "connect" is a reconnect.
  let seenConnect = socket.connected;
  const onConnect = (): void => {
    if (seenConnect) handler();
    seenConnect = true;
  };
  socket.on("connect", onConnect);
  return () => socket.off("connect", onConnect);
}

export function listenForReAuth(onReAuth: () => Promise<void>): () => void {
  const socket = getSocket();

  const onEvent = (event: ServerToClient): void => {
    if (event.type === "auth_required") {
      void onReAuth();
    }
  };

  socket.on("event", onEvent as Parameters<typeof socket.on>[1]);

  return () => {
    socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
  };
}
